"use client";
import React from "react";

import { Box, BoxSx } from "../../foundations";
import { useWarning } from "../../hooks/useWarning";
import { Avatar } from "./Avatar";

interface AvatarGroupProps {
  children: React.ReactElement<typeof Avatar>[];
  size?: "xs" | "sm" | "md" | "lg";
  max?: number;
}

export const AvatarGroup: React.FC<AvatarGroupProps> = ({
  children,
  size = "md",
  max = children.length
}) => {
  const { warn } = useWarning();

  if (max < 0) {
    warn("AvatarGroup: `max` cannot be negative.");
  }

  const displayedAvatars = children.slice(0, max);
  const overflowCount = children.length - max;
  const overlapMargin = React.useMemo<BoxSx>(
    () => ({
      marginLeft: (theme) =>
        `calc(-1 * ${(theme.space as unknown as Record<string, string>)["spacing-3"]})`
    }),
    []
  );

  const groupStyles = React.useMemo<BoxSx>(
    () => ({
      outline: "2px solid",
      outlineColor: "stroke-neutral-default",
      borderRadius: "radius-round"
    }),
    []
  );

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {displayedAvatars.map((child, index) => (
        <Box
          key={child.key || `avatar-${index}`}
          sx={{
            ...(index !== 0 && overlapMargin), // no overlap on first item
            ...groupStyles
          }}
        >
          <Avatar {...child.props} size={size} />
        </Box>
      ))}

      {overflowCount > 0 && (
        <Box
          sx={{
            ...overlapMargin,
            ...groupStyles
          }}
        >
          <Avatar
            key={`avatar-overflow-${overflowCount}`}
            size={size}
            alt={`${overflowCount} more Avatars`}
          >{`+${overflowCount}`}</Avatar>
        </Box>
      )}
    </Box>
  );
};
